import { ExcelController } from "@controllers/Excel.controller";
import { Group } from "@entities";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { CircularProgress, Stack } from "@mui/material";
import { groupAssociationRepo, groupRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { useSessionStorage } from "@shared/frontend/hooks";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { groupCreateUrl } from "@shared/helpers/constants";
import { downloadExcelFile } from "clients/admin/src/desktop/helpers/excel/export";
import {
  countGroupedById,
  useDisplayedIdCounts,
} from "../../../helpers/admin-list-query";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GroupCard from "../../../components/popups/group-card/GroupCard";
import StatusDialog from "../../../components/status-dialog/StatusDialog";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import SelectOpportunitiesTable from "../select-opportunities-table";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { useGroupColumns } from "./columns";
import { getFilters } from "./filters";
import "./GroupsTable.scss";
import "./VolunteersGroupsTable.scss";
import { handleOpportunitiesSelect } from "./methods";
import { useUser } from "../../../contexts/UserProvider";
import { remult } from "remult";
import { TableToolbarTabs } from "../table-tabs/TableToolbarTabs";
import { type RelationalOrJsonEntityFilter } from "@shared/helpers/sql-relations";
import { Roles } from "@shared/Roles";

type GroupsTableTab = "records" | "interested" | "new" | "history";

const GROUPS_TAB_OPTIONS: { value: GroupsTableTab; content: string; }[] = [
  { value: "new", content: "חדשות" },
  { value: "records", content: "רשומות" },
  { value: "interested", content: "מתעניינות" },
  { value: "history", content: "היסטוריה" },
];

const VolunteerGroupsTable: FC = () => {
  const { HyperTable } = useCreateHyperTable(Group);
  const tableInstance = HyperTable.context.current?.table;
  const { currOrganization } = useOrganization();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useSessionStorage<GroupsTableTab>(
    "volunteer-groups-table-selected-tab",
    "records",
  );
  const isHistory = activeTab === "history";
  const { setAlertText, setSeverity } = useAlert();
  const [isSelectOpportunitiesOpen, setIsSelectOpportunitiesOpen] =
    useState<Group>();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [isAddGroup, setIsAddGroup] = useState<boolean>(false);
  const question = useQuestion();
  const { groupId } = useParams<"groupId">();
  const { currentUser } = useUser();
  const loadRegisteredCounts = useCallback(
    (ids: string[]) => countGroupedById(groupAssociationRepo, "groupId", ids),
    [],
  );
  const { counts: registeredCountById, onDisplayedDataChange } =
    useDisplayedIdCounts(loadRegisteredCounts);

  const getMenuActions = useCallback<SingleRowActionGetter<Group>>(
    (row) => {
      if (isHistory) return [];
      return [
        {
          text: "קישור הצטרפות לקבוצה",
          infoTooltip: `העתקת קישור הצטרפות לקבוצה ${row.original.name}`,
          icon: <ContentCopyIcon />,
          onClick: (_e, row) => {
            navigator.clipboard.writeText(
              `https://www.we-me.app/group/${row.original.id}/join`,
            );
            setAlertText("הקישור הועתק ללוח");
            setSeverity("success");
            return {
              refetch: false,
            };
          },
        },
        {
          text: "שיוך למשימות",
          infoTooltip: "שיוך קבוצה זו למשימות",
          icon: <CalendarMonthIcon />,
          onClick: (_, row) => {
            setIsSelectOpportunitiesOpen(row.original);
            return {
              refetch: false,
            };
          },
        },
        {
          text: "עריכה",
          infoTooltip: `עריכת קבוצה ${row.original.name}`,
          icon: <EditIcon />,
          onClick: (_, row) => {
            setSelectedGroup(row.original);
            return {
              refetch: true,
            };
          },
        },
        {
          text: "העברה ללא פעילה",
          infoTooltip: "ההעברה ללא פעילה",
          icon: <DeleteOutlineIcon />,
          onClick: async (_, row) => {
            const yes = await question(
              "האם ברצונך להעביר קבוצה זו ללא פעילות?",
            );
            if (yes) {
              await groupRepo.update(row.original, { isDeleted: true });
              setAlertText("הקבוצה נמחקה בהצלחה");
              setSeverity("success");
              return {
                refetch: true,
              };
            }
          },
        },
        {
          icon: <FileUploadIcon />,
          text: "ייצוא לאקסל",
          infoTooltip: "ייצוא קבוצה זו לאקסל",
          onClick: async () => {
            const excelResult = await ExcelController.exportGroupsToExcel({
              id: row.original.id,
            });
            if (excelResult) {
              downloadExcelFile(excelResult);
            }
            return {
              refetch: false,
            };
          },
        },
      ];
    },
    [isHistory],
  );

  useEffect(() => {
    if (groupId) {
      getGroup();
    }
  }, [groupId]);

  useEffect(() => {
    if (!tableInstance) return;

    const currentVisibility =
      tableInstance.getState().columnVisibility ?? {};

    const desiredVisibility = {
      ...currentVisibility,
      opportunities: activeTab === "records",
      interestedOpportunities: activeTab === "interested",
    };

    const hasChanged =
      currentVisibility.opportunities !== desiredVisibility.opportunities ||
      currentVisibility.interestedOpportunities !==
      desiredVisibility.interestedOpportunities;

    if (!hasChanged) return;

    tableInstance.setColumnVisibility(desiredVisibility);
  }, [activeTab, tableInstance]);

  const tabFilter = useMemo(() => {
    switch (activeTab) {
      case "history":
        return {
          isDeleted: true,
          volunteerOrganizationId: null,
        };
      case "records":
        return {
          isDeleted: false,
          volunteerOrganizationId: null,
          opportunities: { $some: {} },
        };
      case "interested":
        return {
          isDeleted: false,
          volunteerOrganizationId: null,
          interestedOpportunities: {
            $some: { hasDeclinedSurvey: false },
          },
          opportunities: { $none: {} },
        };
      case "new":
      default:
        return {
          isDeleted: false,
          volunteerOrganizationId: null,
          opportunities: { $none: {} },
          interestedOpportunities: {
            $none: { hasDeclinedSurvey: false },
          },
        };
    }
  }, [activeTab]);

  const groupListInclude = useMemo(() => {
    const base = {
      admin: true,
      status: true,
      organization: true,
      team: true,
    };
    if (activeTab === "records") {
      return {
        ...base,
        opportunities: { limit: 20, include: { opportunity: true } },
      };
    }
    if (activeTab === "interested") {
      return {
        ...base,
        interestedOpportunities: { limit: 20, include: { opportunity: true } },
      };
    }
    return base;
  }, [activeTab]);

  const getGroup = async () => {
    if (!groupId) return;
    const group = await groupRepo.findOne({
      where: { id: groupId },
      include: { admin: true },
    });
    setSelectedGroup(group);
    setIsAddGroup(false);
  };

  const buttons = useMemo<ActionButton<Group>[]>(() => {
    if (isHistory) {
      return [
        {
          icon: <UnarchiveIcon />,
          onClick: async ({ whereToUpdate }) => {
            const yes = await question("האם ברצונך לשחזר קבוצות אלו?");
            if (yes) {
              await groupRepo.updateMany({
                where: whereToUpdate,
                set: {
                  isDeleted: false,
                },
              });
              setAlertText("הקבוצה שוחזרה בהצלחה");
              setSeverity("success");
              return {
                refetch: true,
                resetSelection: false,
              };
            }
          },
          title: "שחזור",
        },
      ];
    }

    return [
      {
        icon: <DeleteOutlineIcon />,
        onClick: async ({ whereToUpdate }) => {
          const yes = await question(
            "האם ברצונך להעביר ללא פעילות את קבוצות אלו?",
          );

          if (yes) {
            await groupRepo.updateMany({
              where: whereToUpdate,
              set: {
                isDeleted: true,
              },
            });
            setAlertText("הקבוצות עברו להיסטוריה בהצלחה");
            setSeverity("success");
            return {
              refetch: true,
              resetSelection: false,
            };
          }
        },
        title: "העברה להיסטוריה",
      },
      {
        icon: <FileUploadIcon />,
        onClick: async ({ whereToUpdate }) => {
          const excelResult =
            await ExcelController.exportGroupsToExcel(whereToUpdate);
          if (excelResult) {
            downloadExcelFile(excelResult);
          }

          return {
            refetch: false,
            resetSelection: true,
          };
        },
        title: "ייצוא לאקסל",
      },
    ];
  }, [isHistory, question]);

  const groupButtons = useMemo(
    () => [
      {
        label: "קישור לקבוצות מאורגנות",
        icon: <ContentCopyIcon />,
        onClick: () => {
          navigator.clipboard.writeText(
            groupCreateUrl(currOrganization?.id ?? ""),
          );
          setAlertText("הקישור הועתק ללוח");
          setSeverity("success");
        },
      },
      {
        label: "סטטוסי קבוצות",
        icon: null,
        onClick: () => setIsStatusDialogOpen(true),
      },
      {
        label: "יומן קבוצות",
        icon: null,
        onClick: () => navigate("/groups-calendar"),
      },
    ],
    [navigate, isHistory, currOrganization, setActiveTab],
  );

  const groupColumns = useGroupColumns(
    currOrganization,
    setAlertText,
    setSeverity,
    HyperTable,
    registeredCountById,
  );

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <div className="volunteer-groups-table">
      <HyperTable
        tableId="volunteer-groups-table"
        components={{
          addRowButton: {
            onClick: () => {
              setIsAddGroup(true);
              setSelectedGroup(undefined);
            },
            content: "הוספת קבוצה",
          },
        }}
        findConfig={{
          initialOrderBy: {
            createdAt: "desc",
            name: "asc",
          },
          where: Group.hyperFilter({
            organization: { $id: currOrganization?.id },
            ...tabFilter,
            $or: [remult.isAllowed(Roles.Admins)
              ? {}
              : {
                team: {
                  $is: {
                    id: currentUser?.teams?.map((team) => team.teamId)
                  }
                }
              }],
          }),
          enabled: !!currOrganization,
          include: groupListInclude,
        }}
        onDisplayedDataChange={onDisplayedDataChange}
        onRowClick={(_, row) => {
          setSelectedGroup(row.original);
        }}
        title={<TableHeaderTitles titles={[{ content: "קבוצות מאורגנות" }]} />}
      >
        <HyperTable.Toolbar moreActions={groupButtons}>
          <TableToolbarTabs
            value={activeTab}
            onChange={setActiveTab}
            options={GROUPS_TAB_OPTIONS}
          />
          {getFilters(currOrganization)?.map((filter) =>
            isEntityFilter(filter) ? (
              <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
            ) : (
              <HyperTable.CustomFilter key={filter.id} {...filter} />
            ),
          )}
        </HyperTable.Toolbar>
        <HyperTable.SelectColumn />
        <HyperTable.MenuColumn actions={getMenuActions} />
        {groupColumns.map((column) => (
          <HyperTable.Column key={column.field} {...column} />
        ))}
        <StatusDialog
          open={isStatusDialogOpen}
          onClose={() => setIsStatusDialogOpen(false)}
          type="group"
        />
        <GroupCard
          isEdit={!isAddGroup}
          isOpen={!!selectedGroup || isAddGroup}
          onEditComplete={() => {
            HyperTable.context.current?.refetch("remove");
          }}
          onSaveComplete={() => {
            HyperTable.context.current?.refetch("remove");
          }}
          onClose={() => {
            setSelectedGroup(undefined);
            setIsAddGroup(false);
          }}
          groupDetails={selectedGroup}
        />
        <SelectOpportunitiesTable
          initialSelectedOpportunities={[]}
          open={!!isSelectOpportunitiesOpen}
          closeOnSave
          onSave={(selected) => {
            handleOpportunitiesSelect(
              selected,
              isSelectOpportunitiesOpen,
              setAlertText,
              setSeverity,
            );
          }}
          onClose={() => setIsSelectOpportunitiesOpen(undefined)}
        />
        <HyperTable.ActionsBar buttons={buttons} />
      </HyperTable>
    </div>
  );
};

export default VolunteerGroupsTable;
